import {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  DocumentNode,
  SuspenseQueryHookOptions,
  useSuspenseQuery,
} from '@apollo/client';

import { getError, getTypeOrNull } from './apollo';
import { useEventCallback } from './useEventCallback';
import { replaceFragment } from './utils/composeDocument';

type QueryVariables = {
  filter?: string | null;
  pageSize?: number | null;
  pageToken?: string | null;
  orderBy?: string | null;
};

export type NewStandardListSuspenseHookOptions<TCustomVars = QueryVariables> = {
  filter?: string;
  pageSize?: number;
  orderBy?: string;
  skip?: boolean;
  fetchPolicy?: SuspenseQueryHookOptions['fetchPolicy'];
  fragment?: DocumentNode;
  fragmentName?: string;
} & TCustomVars;

type ListResponse<TItem, TPath extends string> = {
  [K in TPath]: TItem[];
} & {
  nextPageToken: string | null;
};

type NewStandardListSuspenseHookResult<TItem, TPath extends string> = {
  [K in TPath]: TItem[];
} & {
  hasNextPage: boolean;
  nextPage: () => Promise<void>;
  startFetching: () => void;
  stopFetching: () => void;
  isEmpty: boolean;
};

type QueryOptions = {
  fragmentName: string;
};

export function withNewStandardListSuspenseHook<
  /* eslint-disable @typescript-eslint/no-explicit-any -- TData/TCustomVars accept Apollo-generated types whose Variables shapes don't satisfy a stricter Record<string, unknown> constraint */
  TData extends Record<string, any>,
  TDefaultItem,
  TPath extends Extract<keyof TData, string>,
  TCustomVars extends Record<string, any> = QueryVariables,
  /* eslint-enable @typescript-eslint/no-explicit-any */
>(
  query: DocumentNode,
  dataPath: TPath,
  queryOptions: QueryOptions,
): <TItem = TDefaultItem>(
  options?: NewStandardListSuspenseHookOptions<TCustomVars>,
) => NewStandardListSuspenseHookResult<TItem, TPath> {
  return <TItem = TDefaultItem>(
    options: NewStandardListSuspenseHookOptions<TCustomVars> = {} as NewStandardListSuspenseHookOptions<TCustomVars>,
  ): NewStandardListSuspenseHookResult<TItem, TPath> => {
    const {
      filter,
      pageSize,
      orderBy,
      skip,
      fetchPolicy,
      fragment,
      fragmentName,
      ...customVars
    } = options;
    const [isFetching, setIsFetching] = useState(false);

    const activeQuery = useMemo(() => {
      if (fragment && fragmentName) {
        return replaceFragment(
          query,
          queryOptions.fragmentName,
          fragment,
          fragmentName,
        );
      }

      return query;
    }, [fragment, fragmentName]);

    const {
      data,
      error: networkError,
      fetchMore,
    } = useSuspenseQuery<TData, QueryVariables & TCustomVars>(activeQuery, {
      variables: {
        filter: filter ?? null,
        pageSize: pageSize ?? null,
        orderBy: orderBy ?? null,
        ...(customVars as TCustomVars),
      },
      skip,
      fetchPolicy,
    });

    const nestedData = data?.[dataPath];
    const typedData = getTypeOrNull(nestedData) as ListResponse<
      TItem,
      TPath
    > | null;
    const appError = getError(nestedData) ?? undefined;
    const nextPageToken = typedData?.nextPageToken;

    const handleNextPage = useEventCallback(async () => {
      if (nextPageToken && fetchMore) {
        setTimeout(() => {
          startTransition(() => {
            fetchMore({
              // @ts-expect-error pageToken is not in the generic query variables type
              variables: {
                pageToken: nextPageToken,
              },
            });
          });
        });
      }
    });

    useEffect(() => {
      if (nextPageToken && isFetching) {
        handleNextPage();
      }
    }, [isFetching, nextPageToken, handleNextPage]);

    const startFetching = useCallback(() => setIsFetching(true), []);
    const stopFetching = useCallback(() => setIsFetching(false), []);

    const items = useMemo(() => typedData?.[dataPath] ?? [], [typedData]);
    const isEmpty = items.length === 0 && !appError && !networkError;

    const error = appError ?? networkError;
    if (error) throw error;

    return {
      [dataPath]: items,
      hasNextPage: !!nextPageToken,
      nextPage: handleNextPage,
      isEmpty,
      startFetching,
      stopFetching,
    } as NewStandardListSuspenseHookResult<TItem, TPath>;
  };
}
